import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  DataTable,
  DataTableHeader,
  DataTableHeadCell,
  DataTableBody,
  ListPageLoading,
  ListPageEmpty,
  ListPagePagination,
  Input,
} from '@/shared/components/ui';
import {
  useAudienceEntries,
  useUpdateAudienceEntry,
  useDeleteAudienceEntry,
} from '../hooks';
import type { AudienceEntryResponse } from '@troia-v3/types';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

export interface AudienceEntriesTableProps {
  audienceId: string;
}

export const AudienceEntriesTable: React.FC<AudienceEntriesTableProps> = ({ audienceId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const confirm = useConfirm();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading } = useAudienceEntries(audienceId, {
    page,
    limit: 20,
    search: debouncedSearch || undefined,
  });

  const updateEntry = useUpdateAudienceEntry(audienceId);
  const deleteEntry = useDeleteAudienceEntry(audienceId);

  const startEdit = (entry: AudienceEntryResponse) => {
    setEditingId(entry.id);
    setEditValue(entry.name ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (entryId: string) => {
    try {
      await updateEntry.mutateAsync({ entryId, data: { name: editValue.trim() || undefined } });
      toast.success('Entrada atualizada');
      cancelEdit();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao atualizar');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (!(await confirm({ message: 'Tem certeza que deseja excluir esta entrada?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteEntry.mutate(entryId, {
      onSuccess: () => toast.success('Entrada excluída'),
      onError: (err) => toast.error(err.message || 'Erro ao excluir'),
    });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 max-w-md">
          <Input
            icon="search"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por identificador ou nome..."
            data-testid="audience-search"
          />
        </div>
        {data && (
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            {data.total} entrada{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {isLoading && <ListPageLoading message="Carregando entradas..." />}

      {!isLoading && data && data.items.length === 0 && (
        <ListPageEmpty
          icon="inbox"
          message="Nenhuma entrada encontrada"
          description={
            debouncedSearch
              ? 'Nenhuma entrada corresponde à busca atual'
              : 'Adicione entradas manualmente ou importe um arquivo CSV'
          }
        />
      )}

      {!isLoading && data && data.items.length > 0 && (
        <DataTable>
          <DataTableHeader>
            <DataTableHeadCell>Identificador</DataTableHeadCell>
            <DataTableHeadCell>Nome</DataTableHeadCell>
            <DataTableHeadCell>Origem</DataTableHeadCell>
            <DataTableHeadCell>Adicionado em</DataTableHeadCell>
            <DataTableHeadCell align="right">Ações</DataTableHeadCell>
          </DataTableHeader>
          <DataTableBody>
            {data.items.map((entry, index) => (
              <tr
                key={entry.id}
                className={index > 0 ? 'border-t' : ''}
                style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
              >
                <td className="px-4 py-3">
                  <span className="text-sm font-mono" style={{ color: 'var(--theme-card-text)' }}>
                    {entry.identifier}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {editingId === entry.id ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(entry.id);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          data-testid="audience-entry-edit"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => saveEdit(entry.id)}
                        className="text-xs px-2 py-1"
                        style={{ color: 'var(--theme-primary)' }}
                      >
                        Salvar
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-xs px-2 py-1"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEdit(entry)}
                      className="text-sm hover:underline text-left"
                      style={{ color: entry.name ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
                    >
                      {entry.name ?? '— clique para editar'}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                    {entry.source === 'import' ? 'Importação' : 'Manual'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                    {new Date(entry.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => handleDelete(entry.id)}
                    className="text-xs hover:underline"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </DataTableBody>
        </DataTable>
      )}

      {/* Paginação F48: totalItems/pageSize/itemLabel — "Mostrando X–Y de N entradas" */}
      {!isLoading && data && (data.total ?? 0) > 0 && (
        <div className="mt-4">
          <ListPagePagination
            currentPage={data.page}
            totalPages={data.totalPages}
            onPageChange={setPage}
            totalItems={data.total}
            pageSize={20}
            itemLabel="entradas"
          />
        </div>
      )}
    </div>
  );
};
